import { Body, Link, ListItem, UnorderedList } from '@hover/blueprint';
import React from 'react';
import SectionContainer from '../SectionContainer';

interface Props {
  hoverEmail: string;
}

export const ContentOct2020: React.FC<Props> = ({ hoverEmail }) => (
  <>
    <SectionContainer
      id="privacy-personal-information"
      heading="Personal information we collect"
    >
      <Body mb={500}>
        We collect personal information directly from you, from HOVER users that
        add a property to their account in furtherance of working with a
        Resident, and automatically through your use of our Service.
      </Body>
      <Body mb={500}>
        <b>Categories of personal information we may collect:</b> Information
        about the property (such as postal address), information about uploaded
        images (such as the geolocation of the camera when a picture is taken),
        contact information about the property’s resident (such as name or email
        address), and information about a business performing the data
        collection (such as the organization name and contact information of its
        representative(s)).
      </Body>
      <Body mb={500}>
        <b>When you create an account:</b> A Resident or business must create an
        account to use HOVER. When you do so, we collect and store personal data
        you share with us, such as your name and email address, the property’s
        postal address and associated contact information you upload, as well as
        any additional information you add to your profile or information about
        properties you add to the account.
      </Body>
      <Body mb={500}>
        <b>When another HOVER user uploads information about your property:</b>{' '}
        HOVER works with many businesses that provide property services
        including contractors, assessors, and insurers. They use HOVER by adding
        properties to their account and storing information associated with
        these properties including names, email addresses, residential addresses
        and images of the home.
      </Body>
      <Body mb={500}>
        <b>When images are added to an account:</b> We collect and store images
        uploaded to HOVER as well as metadata and sensor information from your
        device (for example, image metadata and the location where the image was
        taken) in order to create a 3D model of a property. Please take care to
        ensure the images you upload to HOVER do not contain any information
        that itself can identify you or any other individual.
      </Body>
      <Body mb={500}>
        <b>When you pay for services:</b> We use a third-party service to
        process credit card and payment information for any payments made on our
        website. We do not access or store credit card data.
      </Body>
      <Body>
        <b>When you use the HOVER Service:</b> We collect information about how
        you use the service whether you are logged into a HOVER account or just
        visiting our website. This may include log data (such as your device ID,
        IP address, browser information, device hardware, and software type),
        usage data (such as which pages you click on or how you use our mobile
        app), precise geo-location information, and Cookie Data. We may also
        periodically obtain both personal and non-personal information about you
        from other businesses, business partners, contractors, and other third
        parties.
      </Body>
    </SectionContainer>
    <SectionContainer
      id="privacy-how-we-use"
      heading="How we use personal information"
    >
      <Body mb={500}>
        We process personal data in order to provide the HOVER services, for
        example:
      </Body>
      <UnorderedList ml={0}>
        <ListItem>
          To allow you to log into and access your HOVER account, profile, and
          property information, to process payments, or to fulfill our
          obligations in our Terms of Use.
        </ListItem>
        <ListItem>
          To improve, personalize, develop and perform analytics on the HOVER
          platform and services.
        </ListItem>
        <ListItem>
          To offer features related to a property and to make a property
          available to others on the HOVER platform.
        </ListItem>
        <ListItem>
          To determine a location for the property that doesn’t include your
          personal information.
        </ListItem>
        <ListItem>
          To connect you with other HOVER users and Hover Inc. business partners
          when you choose to do so.
        </ListItem>
        <ListItem>
          To contact you about your HOVER account, in response to a customer
          support request, and to offer you additional services, unless you
          opt-out of marketing communications.
        </ListItem>
        <ListItem>Determining relevant content to display to you.</ListItem>
      </UnorderedList>
    </SectionContainer>
    <SectionContainer
      id="privacy-when-we-share-and-use"
      heading="When we share your information"
    >
      <Body mb={500}>
        We only share personal data in the following circumstances:
      </Body>
      <Body mb={500}>
        <b>With your consent:</b> Personal information you upload directly to
        HOVER may be shared with third parties you authorize us to share data
        with such as a Resident, home improvement company or professional you
        purchase services from, or a social media site. Your personal
        information will be covered by those parties’ privacy policies.
      </Body>
      <Body mb={500}>
        <b>With the consent of a HOVER user:</b> Personal information related to
        a property that is uploaded by a HOVER User, with that user’s consent,
        may be shared with additional parties they authorize in order to provide
        services to that property.
      </Body>
      <Body mb={500}>
        <b>With a Hover Inc. partner under an enterprise agreement:</b> If your
        use of HOVER is being sponsored and/or paid for by a Hover Inc. partner,
        including if you are using a HOVER App sponsored by a partner, HOVER
        provides your personal information related to your use of HOVER to that
        Partner, its affiliates and/or applicable service providers. The Partner
        will have the ability to access, disclose, restrict, monitor, compile
        information and generate reports from your information and your general
        usage of the HOVER Platform and may use the information for their own
        business and marketing purposes, as described in the enterprise
        agreement between Hover Inc. and that Partner.
      </Body>
      <Body mb={500} isBold>
        If you do not agree to the sharing of your information or do not wish to
        be contacted by the Hover Inc. Partner, or its affiliates or service
        providers, do not use HOVER in this manner.
      </Body>
      <Body mb={500}>
        <b>For processing purposes:</b> We transfer personal data to service
        providers who process it for us, based on our instructions, and in
        compliance with this policy and any other appropriate confidentiality
        and security measures. These partners provide us with services globally,
        including customer support, image processing, 3D model creation,
        information technology, payments, sales, marketing, data analysis,
        research, and surveys.
      </Body>
      <Body mb={500}>
        <b>In response to legal process:</b> We may disclose the information we
        collect from you in order to comply with valid legal processes, such as
        in response to a court order or a subpoena. Our policy is to notify you
        of legal process seeking access to your information unless we are
        prohibited by law from doing so. In cases where a court order specifies
        a non-disclosure period, we provide delayed notice after the expiration
        of the non-disclosure period.
      </Body>
      <Body mb={500}>
        <b>To protect us and others:</b> We may disclose the information we
        collect from you where we believe it is necessary to investigate,
        prevent, or take action regarding illegal activities, suspected fraud,
        situations involving potential threats to the safety of any person,
        violations of our Terms of Use or this Policy, or as evidence in
        litigation in which HOVER is involved.
      </Body>
      <Body>
        <b>If our business changes:</b> If we are involved in a merger,
        acquisition, or sale of assets, we will continue to take measures to
        protect the confidentiality of personal information and give affected
        users notice before transferring any personal information to a new
        entity.
      </Body>
    </SectionContainer>
    <SectionContainer
      id="privacy-third-party-tools"
      heading="Third Party Tools and Cookies"
    >
      <Body mb={500}>
        <b>Third-Party Tools:</b> We use automated devices and applications to
        operate and evaluate usage of our Service. We use these tools to help us
        improve the Service, performance, and user experiences. Among these
        tools are Mixpanel, Google Analytics and Branch.
      </Body>
      <UnorderedList ml={0} mb={500}>
        <ListItem>
          You may opt-out of Mixpanel’s analytics tracking by visiting{' '}
          <Link fontWeight={400} href="https://mixpanel.com/optout">
            https://mixpanel.com/optout.
          </Link>{' '}
          If you choose to use the Mixpanel opt-out, you
        </ListItem>
        <ListItem>
          You may opt-out of Google Analytics by downloading the Google
          Analytics opt-out tool at{' '}
          <Link
            href="https://tools.google.com/dlpage/gaoptout."
            fontWeight={400}
          >
            https://tools.google.com/dlpage/gaoptout.
          </Link>
        </ListItem>
        <ListItem>
          We use information from your browser or device to allow Branch to
          connect users with the HOVER app. You can opt-out of Branch by
          visiting{' '}
          <Link href="https://branch.app.link/optout." fontWeight={400}>
            https://branch.app.link/optout.
          </Link>
        </ListItem>
      </UnorderedList>
      <Body mb={500}>
        <b>Cookies:</b> Cookies are small text files used to store data for a
        variety of purposes.
      </Body>
      <Body mb={500}>
        The HOVER mobile app and website utilize functional cookies to enable
        the HOVER services and enhance the user experience such as maintaining
        account settings, login and authentication during logged-in periods.
        These cookies do not interact with browsing activity on non-HOVER
        platforms such as third party websites.
      </Body>
      <Body mb={500}>
        The HOVER website utilizes, when enabled, advertising cookies to inform
        targeted advertising initiatives. These cookies further track
        advertising performance to deliver relevant HOVER products and services
        through third party platforms.
      </Body>
      <Body mb={500}>
        To learn more about the various cookies engaged by the HOVER services,
        and to control your cookie settings, visit the HOVER Trust Center.
      </Body>
      <Body>
        You can also manage your cookies locally by adjusting your browser
        settings, or you can opt-out of targeted advertising through cookies by
        visiting{' '}
        <Link href="networkadvertising.org/choices" fontWeight={400}>
          networkadvertising.org/choices
        </Link>{' '}
        or{' '}
        <Link href="aboutads.info/choices" fontWeight={400}>
          aboutads.info/choices
        </Link>
        .
      </Body>
    </SectionContainer>
    <SectionContainer
      id="privacy-third-party-links"
      heading="Third-Party Links"
    >
      <Body>
        Our Service may contain links to third-party websites or social media
        outlets. Any access to and use of such linked websites is not governed
        by this Policy but instead is governed by the privacy policies of those
        third-party websites. We are not responsible for the information
        practices of such third-party websites.
      </Body>
    </SectionContainer>
    <SectionContainer id="privacy-security" heading="Security">
      <Body mb={500}>
        We have implemented reasonable precautions and follow security best
        practices to protect the information we collect from loss, misuse, and
        unauthorized access, disclosure, alteration, and destruction. We deploy
        network security and data encryption standards that follow industry best
        practices. All network access to and from HOVER is secured using SSL.
        Our data storage systems are also encrypted to enforce an extra layer of
        security for your personal information. Please be aware that despite our
        best efforts, no data security measures can guarantee security.
      </Body>
      <Body>
        You should take steps to protect against unauthorized access to your
        password, phone, and computer by, among other things, signing off after
        using a shared computer, choosing a robust password that nobody else
        knows or can easily guess, and keeping your log-in and password private.
        We are not responsible for any lost, stolen, or compromised passwords or
        for any activity on your account via unauthorized password activity.
      </Body>
    </SectionContainer>
    <SectionContainer
      id="privacy-data-retention"
      heading="Data Retention and Account Deactivation"
    >
      <Body mb={500}>
        You can choose to deactivate your account by contacting{' '}
        <Link href={`mailto:${hoverEmail}`} fontWeight={400}>
          {hoverEmail}
        </Link>{' '}
        After your account is deactivated, you will not be able to sign in to
        our website or access any of your personal information. We will still
        retain information associated with your account for analytical purposes
        and recordkeeping integrity, to allow you to reactivate your account, as
        well as to prevent fraud, collect any fees owed, enforce our Terms of
        Use, take actions we deem necessary to protect the integrity of our web
        site or our users, or take other actions otherwise permitted by law. In
        addition, if certain information has already been provided to third
        parties as described in this Privacy Policy, retention of that
        information will be subject to those third parties’ policies and rights.
      </Body>
      <Body mb={500}>
        If your personal information resides in an account, you may access and
        control certain aspects of that account. For example, if a 3D model of
        your home is created by a business, you may delete your personal
        information associated with that model, or share that model and its
        measurements with other businesses’ accounts. It’s your data.
      </Body>
      <Body mb={500}>
        We retain personal information as long as you like. We do not delete
        information unless specifically requested to do so with a verified
        request.
      </Body>
      <Body>
        Retention duration is to ensure we can fulfill consumer queries on what
        information we have collected, or to verify a deletion request. In some
        cases, we may de-identify, aggregate, or otherwise anonymize personal
        information, or retain solely for internal purposes pursuant to
        California Consumer Privacy Act § 1798.105(d).
      </Body>
    </SectionContainer>
    <SectionContainer id="privacy-children" heading="Children">
      <Body>
        HOVER is targeted to adults, and we do not knowingly collect personal
        information from children under the age of 13. If you believe we have
        collected personal information from a child under the age of 13, please
        contact us immediately at{' '}
        <Link fontWeight={400} href="mailto:privacy@hover.to">
          privacy@hover.to
        </Link>
      </Body>
    </SectionContainer>
    <SectionContainer
      id="privacy-your-california"
      heading="Your California Privacy Rights"
    >
      <Body mb={500}>
        This Privacy Policy and our practices comply with the California
        Consumer Privacy Act (CCPA), which requires that we provide California
        residents with notice that you have the right to:
      </Body>
      <UnorderedList mb={500}>
        <ListItem>opt-out of the sale of personal information,</ListItem>
        <ListItem>
          know about personal information collected, disclosed or sold,
        </ListItem>
        <ListItem>to request deletion of personal information, and</ListItem>
        <ListItem>
          to be treated without discrimination should you exercise these rights
        </ListItem>
      </UnorderedList>
      <Body>
        To make a request under the California Consumer Privacy Act, please
        contact us at{' '}
        <Link fontWeight={400} href="mailto:privacy@hover.to">
          privacy@hover.to
        </Link>{' '}
        with the Subject Line: CCPA.
      </Body>
    </SectionContainer>

    <SectionContainer
      id="privacy-privacy-request"
      heading="How To Make A Privacy Request"
    >
      <Body mb={500}>
        Please submit privacy requests using this{' '}
        <Link href="https://preferences.hover.to/privacy" fontWeight={400}>
          Web Form.
        </Link>
      </Body>
      <Body mb={500}>
        You may also submit privacy requests or contact us with questions
        relating to this Privacy Policy by sending an email to our privacy team
        at{' '}
        <Link fontWeight={400} href="mailto:privacy@hover.to">
          privacy@hover.to
        </Link>{' '}
        or by calling our toll-free number (866-665-0340).
      </Body>
      <Body>You may also write to us at the address below.</Body>
    </SectionContainer>
    <SectionContainer id="privacy-contact-us" heading="Contact us">
      <Body mb={500}>
        You may contact us with any questions relating to this Privacy Policy by
        contacting us at{' '}
        <Link fontWeight={400} href="mailto:privacy@hover.to">
          privacy@hover.to
        </Link>
      </Body>
      <Body>HOVER, Inc.</Body>
      <Body>255 Bush St, 7th Floor</Body>
      <Body>San Francisco, CA 94104</Body>
    </SectionContainer>
  </>
);
